import Axios from "axios";
import {InterestFeedbackOption, InterestMemoryAddRequest, InterestMemorySuggestRequest, InterestMemorySuggestion} from "../domain/InterestMemory";
import VulReportAiSummary from "../domain/VulReportAiSummary";
import VulReport from "../domain/VulReport";
import QueryOptions from "../sal-ui/QueryOptions";
import PagedResult from "./PagedResult";

class VulReportService {

    readonly basePath = "/vul-reports";

    public getInterestFeedback(id: string): Promise<InterestFeedbackOption[]> {
        return Axios.get(`${this.basePath}/${id}/interest-feedback`).then(value => value.data.interests);
    }

    public suggestInterestMemory(id: string, request: InterestMemorySuggestRequest): Promise<InterestMemorySuggestion[]> {
        return Axios.post(`${this.basePath}/${id}/interest-feedback/suggest`, request).then(value => value.data.suggestions);
    }

    public saveInterestMemory(id: string, request: InterestMemoryAddRequest): Promise<void> {
        return Axios.post(`${this.basePath}/${id}/interest-feedback/save`, request);
    }

    public add(vulReport: VulReport): Promise<string> {
        return Axios.post(`${this.basePath}`, vulReport).then(value => value.data.id)
    }

    public update(vulReportId: string, vulReport: VulReport): Promise<void> {
        return Axios.put(`${this.basePath}/${vulReportId}`, vulReport);
    }

    public get(id: string): Promise<VulReport> {
        return Axios.get(`${this.basePath}/${id}`)
            .then(value => VulReport.fromPOJO(value.data))
    }

    public delete(vulReport: VulReport): Promise<void> {
        return Axios.delete(`${this.basePath}/${vulReport.id}`);
    }

    public regenerateItems(vulReport: VulReport): Promise<void> {
        return Axios.post(`${this.basePath}/${vulReport.id}/regenerate-items`);
    }

    public getAiSummary(vulReport: VulReport): Promise<VulReportAiSummary> {
        return Axios.get(`${this.basePath}/${vulReport.id}/ai-summary`)
            .then(value => VulReportAiSummary.fromPOJO(value.data));
    }

    public regenerateAiSummary(vulReport: VulReport, summarizerId?: string): Promise<VulReportAiSummary> {
        return Axios.post(`${this.basePath}/${vulReport.id}/ai-summary/regenerate`, {summarizerId: summarizerId ?? null})
            .then(value => VulReportAiSummary.fromPOJO(value.data));
    }

    public sendReport(vulReport: VulReport, targetType: "EMAIL", target: string): Promise<void> {
        return Axios.post(`${this.basePath}/${vulReport.id}/send`, {targetType, target});
    }

    public countItems(vulReport: VulReport, profilePreviewId?: string, itemTypes?: string[]): Promise<{ newItems: number, modifiedItems: number }> {
        const params = new URLSearchParams();

        if (profilePreviewId) {
            params.append("profilePreviewId", profilePreviewId);
        }

        if (itemTypes) {
            itemTypes.forEach(itemType => params.append("itemType", itemType));
        }

        const queryString = params.size > 0 ? `?${params.toString()}` : "";

        return Axios.get(`${this.basePath}/${vulReport.id}/item-count${queryString}`).then(value => value.data);
    }

    public getList(queryOptions: QueryOptions): Promise<PagedResult<VulReport>> {
        return Axios.get(`${this.basePath}?${queryOptions.toQueryParams()}`)
            .then(value => {
                const objectList: VulReport[] = [];

                value.data.data.forEach((objectJson: object) => {
                    objectList.push(VulReport.fromPOJO(objectJson));
                });

                return new PagedResult(value.data.total, objectList);
            })
    }

}

export default VulReportService;
